<script setup lang="ts">
import { computed } from "vue";
import type { DatabaseEducation } from "~/typings/database.d";
import { useI18n } from "~/composables/useI18n";

const t = useI18n();

interface EducationListProps {
  items: DatabaseEducation[];
}

const props = defineProps<EducationListProps>();

const sortedItems = computed(() =>
  [...props.items].sort((a, b) => {
    // null endDate (in progress) floats to top
    if (!a.endDate && !b.endDate) {
      return 0;
    }
    if (!a.endDate) {
      return -1;
    }
    if (!b.endDate) {
      return 1;
    }
    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
  })
);
</script>

<template>
  <ul :aria-label="t('learningPage.educationHeading')">
    <li
      v-for="edu in sortedItems"
      :key="edu.startDate + edu.degreeName"
      class="relative pl-6 border-l-2 border-secondary-200 dark:border-secondary-700 pb-6 last:pb-0"
    >
      <!-- Timeline dot -->
      <span
        class="absolute left-[-5px] top-1.5 w-2 h-2 rounded-full bg-secondary-400 dark:bg-secondary-500"
        aria-hidden="true"
      />

      <!-- In Progress badge / date range -->
      <div class="flex flex-wrap items-center gap-2 mb-1">
        <UiOngoingBadge v-if="!edu.endDate">
          {{ t("learningPage.inProgressLabel") }}
        </UiOngoingBadge>
        <span class="text-sm text-gray-500 dark:text-gray-400">
          <time :datetime="edu.startDate">{{ edu.startDate }}</time>
          <template v-if="edu.endDate">
            – <time :datetime="edu.endDate">{{ edu.endDate }}</time>
          </template>
        </span>
        <UiPill color="gray">{{ edu.type }}</UiPill>
        <UiOngoingBadge v-if="edu.completed !== false" variant="completed">
          {{ t("learningPage.completedLabel") }}
        </UiOngoingBadge>
        <UiOngoingBadge v-else variant="attended">
          {{ t("learningPage.attendedLabel") }}
        </UiOngoingBadge>
      </div>

      <div class="flex flex-col gap-y-1">
        <!-- Degree + institution -->
        <UiTitle level="4" class="text-md font-semibold leading-snug">
          {{ edu.degreeName }}
        </UiTitle>
        <UiParagraph class="text-sm">
          {{ edu.fieldOfStudy }}
          <template v-if="edu.school?.name">
            –
            <a
              v-if="edu.school.url"
              :href="edu.school.url"
              class="hover:underline text-primary-600 dark:text-primary-400"
              target="_blank"
              rel="noopener noreferrer"
              >{{ edu.school.name }}</a
            >
            <span v-else>{{ edu.school.name }}</span>
          </template>
        </UiParagraph>
      </div>

      <!-- Short description if present -->
      <UiParagraph
        v-if="edu.shortDescription || edu.description"
        variant="soft"
        class="mt-1 text-sm"
      >
        {{ edu.shortDescription || edu.description }}
      </UiParagraph>
    </li>
  </ul>
</template>
